import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { useTranslation } from '@/i18n';
import { ApiError } from '@/lib/api/types';
import { describeAccess, useAccessNames } from '@/lib/flightAccess';
import type { FlightAccess } from '@/types/flightAccess';
import type { UserInfo } from '@/types/user';
import { deleteFlightAccess, saveFlightAccess } from './api';
import type { FlightAccessWriteResult } from './api';
import {
  ACCESS_FIELD_OF,
  accessProblems,
  cardOfDraft,
  draftOfAccess,
  errorsAfterChange,
  FlightAccessEditor,
  isSameAccess,
} from './FlightAccessEditor';
import type { AccessDraft, AccessErrors, AccessField } from './FlightAccessEditor';
import { attachErrors } from './form';
import styles from './FlightAccess.module.css';

/**
 * The "Flight access" section of an account's panel.
 *
 * **Its own form with its own Save**, beside the account form rather than inside it.
 * It is a different resource behind a different endpoint, with its own refusals: one
 * Save for both would have to report a half-success — the name saved, the access not
 * — through a button that promised one thing.
 *
 * An administrator's access cannot be restricted, so for one this section explains
 * that instead of offering a form the server refuses. The one thing it does offer
 * there is the clean-up: a restriction stored for an account that has since been
 * given the ADMIN role is ignored, not gone, and applies again if the role is taken
 * away — which an administrator should be able to see, and remove.
 */

/**
 * A selection carried over from a create whose second write failed.
 *
 * The account exists by then and the panel switches to it, which remounts this
 * section — so what was chosen, and why the server refused it, are handed across
 * rather than lost with the dialog.
 */
export interface AccessCarry {
  draft: AccessDraft;
  errors: AccessErrors;
  message: string | null;
}

/** Where this section's access comes from — the list the screen loaded. */
export interface AccessBinding {
  /** The SAVED access; `null` while the list is loading or after it failed. */
  access: FlightAccess | null;
  isLoading: boolean;
  hasFailed: boolean;
  reload: () => void;
  onSaved: (access: FlightAccess) => void;
  carry: AccessCarry | null;
}

export const FlightAccessSection = ({ user, binding }: { user: UserInfo; binding: AccessBinding }) => {
  const { t, formatDateTime } = useTranslation();
  const headingId = useId();
  const { access } = binding;

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.sectionHead}>
        <h3 id={headingId} className={styles.sectionTitle}>
          {t('users.access.title')}
        </h3>

        {/* Who touched it last. The API records this on every save, and an access is
            exactly the kind of setting someone later asks "who changed that?" about. */}
        {access && (
          <span className={styles.stamp}>
            {access.updated
              ? t('users.access.changed', {
                  date: formatDateTime(access.updated),
                  name: access.updatedBy ?? '—',
                })
              : t('users.access.neverSet')}
          </span>
        )}
      </div>

      {access === null ? (
        binding.hasFailed ? (
          <div className={styles.actions}>
            <p className={styles.error} role="alert">
              {t('users.access.errorLoad')}
            </p>
            <Button size="sm" onClick={binding.reload}>
              {t('users.access.retry')}
            </Button>
          </div>
        ) : (
          <p className={styles.note}>{t('common.loading')}</p>
        )
      ) : user.role === 'ADMIN' ? (
        <AdminAccess user={user} access={access} onSaved={binding.onSaved} />
      ) : (
        <AccessForm user={user} access={access} onSaved={binding.onSaved} carry={binding.carry} />
      )}
    </section>
  );
};

/** The administrator's case: nothing to set, possibly something to clean up. */
const AdminAccess = ({
  user,
  access,
  onSaved,
}: {
  user: UserInfo;
  access: FlightAccess;
  onSaved: (access: FlightAccess) => void;
}) => {
  const { t } = useTranslation();
  const names = useAccessNames(access.airports);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<'cleared' | 'failed' | null>(null);

  const clear = async () => {
    setIsSubmitting(true);
    setOutcome(null);

    try {
      const result = await deleteFlightAccess(user.id);
      if (result.ok) {
        onSaved(result.access);
        setOutcome('cleared');
      } else {
        setOutcome('failed');
      }
    } catch {
      setOutcome('failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <p className={styles.note}>{t('users.access.adminNote')}</p>

      {access.restricted && (
        <div className={styles.actions}>
          <p className={styles.warning}>
            {t('users.access.adminStored', { description: describeAccess(access, t, names) })}
          </p>
          <Button size="sm" onClick={() => void clear()} disabled={isSubmitting}>
            {t('users.access.clearStored')}
          </Button>
        </div>
      )}

      {outcome === 'cleared' && (
        <p className={styles.saved} role="status">
          {t('users.access.cleared')}
        </p>
      )}
      {outcome === 'failed' && (
        <p className={styles.error} role="alert">
          {t('users.access.saveFailed')}
        </p>
      )}
    </>
  );
};

/**
 * The form, for every role but ADMIN.
 *
 * Seeded once from the saved access — or from a carried-over create — and then the
 * reader's. The saved access stays the thing it is diffed against, so "nothing has
 * changed" is judged against what the server holds, not what the form started with.
 */
const AccessForm = ({
  user,
  access,
  onSaved,
  carry,
}: {
  user: UserInfo;
  access: FlightAccess;
  onSaved: (access: FlightAccess) => void;
  carry: AccessCarry | null;
}) => {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<AccessDraft>(() => carry?.draft ?? draftOfAccess(access));
  const [errors, setErrors] = useState<AccessErrors>(() => carry?.errors ?? {});
  const [formError, setFormError] = useState<string | null>(() => carry?.message ?? null);
  const [saved, setSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const change = (next: AccessDraft, field: AccessField) => {
    setDraft(next);
    // The message was about the value that was refused; this is no longer it.
    setErrors((current) => errorsAfterChange(current, field, next, t));
    setFormError(null);
    setSaved(false);
  };

  /**
   * Spreads a refusal over the fields it names.
   *
   * `errorMessage` is only the FIRST of the field errors repeated, so once those are
   * under their fields it is not said again above them. It is the message only when
   * nothing could be attached — an unknown user, say.
   */
  const report = (result: Extract<FlightAccessWriteResult, { ok: false }>) => {
    const { attached, loose } = attachErrors(result.errors, ACCESS_FIELD_OF);

    setErrors(attached);
    setFormError(
      loose.length > 0
        ? loose.join(' ')
        : Object.keys(attached).length > 0
          ? null
          : (result.message ?? t('users.access.saveFailed')),
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const problems = accessProblems(draft, t);
    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      setFormError(null);
      return;
    }

    // A save that writes nothing should say so rather than look like it worked — and
    // it would stamp the access as changed by this administrator when it was not.
    if (isSameAccess(draft, access)) {
      setFormError(t('users.access.nothingChanged'));
      return;
    }

    setErrors({});
    setFormError(null);
    setSaved(false);
    setIsSubmitting(true);

    try {
      const result = await saveFlightAccess(user.id, cardOfDraft(draft));

      if (result.ok) {
        onSaved(result.access);
        setSaved(true);
        return;
      }

      report(result);
    } catch (caught) {
      // The request failed, not the selection. Blaming a field would send the reader
      // to correct something that is fine.
      const status = caught instanceof ApiError ? caught.status : 0;
      setFormError(status === 0 ? t('users.panel.networkError') : t('users.panel.serverError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={(event) => void handleSubmit(event)} noValidate>
      {formError && (
        <p className={styles.error} role="alert">
          {formError}
        </p>
      )}

      <FlightAccessEditor value={draft} onChange={change} errors={errors} disabled={isSubmitting} />

      {saved && (
        <p className={styles.saved} role="status">
          {t('users.access.saved')}
        </p>
      )}

      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? t('users.panel.saving') : t('users.access.save')}
        </Button>
      </div>
    </form>
  );
};
